import { BetaFeatureChip } from "../shared-components/BetaFeature";
import { CreateWithBudButton } from "./assistant/components/CreateWithBudButton";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Chip,
} from "@mui/material";
import {
  Add,
  Publish,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { FruitifyContainer } from "../shared-components/Containers";
import { useAuthenticationStore } from "../stores/auth";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../api";
import StatBox from "../shared-components/StatBox";
import Grid from "@mui/material/Grid";
import {
  calculateProgressPercent,
  formatWeekDifference,
  getWeekDiffColor,
} from "../utils/formatters";
import { withPageTracking } from "../services/postHog";
import { UpcomingAssignmentsAndQuizzes } from "../shared-components/UpcomingAssignmentsAndQuizzes";
import { ProfilePicture } from "./account/components/ProfilePicture";
import { checkHasApprovedSubscription } from "./auth/Utils";
import { useHelpStore } from "../stores/help";

export function UnapprovedSubscriptionChecklist({ status }) {
  const navigate = useNavigate();
  const reOpen = useHelpStore((state) => state.openQuickStart);

  const onboardingSteps = [
    {
      key: "assignmentCreated",
      label: "Create an assignment",
      description:
        "Create your first assignment for classwork, homework, or assessments.",
      actionLabel: "Start",
      onClick: () => reOpen(),
    },
    {
      key: "answersUploaded",
      label: "Upload answers",
      description: "Upload student answers so Fruitify can generate feedback.",
      tooltip: "Upload student work for marking.",
    },
    {
      key: "feedbackDownloaded",
      label: "Download feedback",
      description:
        "Review your generated feedback and download it from the Actions menu on the assignment page.",
      tooltip: "Generate and download feedback for your students.",
    },
  ];

  const onboardingComplete = onboardingSteps.every(
    (step) => status?.[step.key],
  );

  const { assignmentsLevel, answersLevel } = status;

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "2fr 1fr",
          },
          gap: 2,
        }}
      >
        <Card>
          <CardContent>
            {!onboardingComplete ? (
              <>
                <Typography variant="h5" gutterBottom>
                  Getting started
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Complete these steps to start getting value from Fruitify. You
                  can also use the quick launch button at the bottom of the
                  screen.
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={
                    (onboardingSteps.filter((step) => status?.[step.key])
                      .length /
                      onboardingSteps.length) *
                    100
                  }
                  sx={{ mb: 3 }}
                />

                <Stack spacing={2}>
                  {onboardingSteps.map((step) => {
                    const complete = Boolean(status?.[step.key]);

                    return (
                      <Box
                        key={step.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          borderRadius: 1,
                          backgroundColor: complete
                            ? "transparent"
                            : "action.hover",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          {complete ? (
                            <CheckCircle color="success" />
                          ) : (
                            <RadioButtonUnchecked color="disabled" />
                          )}

                          <Box>
                            <Typography>{step.label}</Typography>

                            <Typography variant="body2" color="text.secondary">
                              {step.description}
                            </Typography>
                          </Box>
                        </Box>

                        {!complete && step.actionLabel && (
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={step.onClick}
                            >
                              {step.actionLabel}
                            </Button>
                            {step.key === "assignmentCreated" && (
                              <CreateWithBudButton kind="assignment">
                                Create with Bud
                              </CreateWithBudButton>
                            )}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h5" gutterBottom>
                  Get more from Fruitify
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Continue building momentum and unlock more value from
                  Fruitify.
                </Typography>

                <Stack spacing={3}>
                  {[
                    {
                      label: "Assignments",
                      data: assignmentsLevel,
                    },
                    {
                      label: "Answers",
                      data: answersLevel,
                    },
                  ].map(({ label, data }) => (
                    <Box key={label}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2">{label}</Typography>

                        <Chip
                          size="small"
                          label={`Level ${data?.level}`}
                          color="primary"
                        />
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={data?.percentage || 0}
                      />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {data?.current || 0}/{data?.target}{" "}
                        {label.toLowerCase()}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Save time and grow your team
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sync classes from your MIS, invite colleagues, and manage your
              account.
            </Typography>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate("/account/home")}
            >
              Account settings
            </Button>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

function DashboardStatBox(props) {
  return <StatBox cardVariant="shadow" {...props} />;
}
export function DashboardStats({ stats }) {
  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 6, md: 3 }}>
        <DashboardStatBox
          title="Assignments due"
          stat={stats.assignmentsDueStats?.current}
          footer={formatWeekDifference(stats.assignmentsDueStats)}
          footerColor={getWeekDiffColor(stats.assignmentsDueStats)}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DashboardStatBox
          title="Answers reviewed"
          stat={`${calculateProgressPercent(stats.answersViewedStats)} %`}
          footer={
            <LinearProgress
              variant="determinate"
              value={calculateProgressPercent(stats.answersViewedStats)}
            />
          }
        />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DashboardStatBox
          title="Questions created"
          stat={stats.questionsCreatedStats?.current}
          footer={formatWeekDifference(stats.questionsCreatedStats)}
          footerColor={getWeekDiffColor(stats.questionsCreatedStats)}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <DashboardStatBox
          title="Questions reviewed"
          stat={stats.questionsReviewedStats?.current}
          footer={formatWeekDifference(stats.questionsReviewedStats)}
          footerColor={getWeekDiffColor(stats.questionsReviewedStats)}
        />
      </Grid>
    </Grid>
  );
}

const TeacherDashboard = () => {
  const { user } = useAuthenticationStore();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [quizGroups, setQuizGroups] = useState([]);
  const hasApprovedSubscription = checkHasApprovedSubscription();
  const [approvalChecklist, setApprovalChecklist] = useState({});
  const apiClient = useApiClient();
  const roleAssignment = user.info.activeContext?.roleAssignment;

  const fetchUserStats = async () => {
    const data = await apiClient.users.dashboardStats();
    setUserStats(data?.dashboardStats);
    setApprovalChecklist(data?.approvalChecklist);
    setAssignments(data?.activeRoleData.assignments);
    setQuizGroups(data?.activeRoleData.quizGroups);
  };

  useEffect(() => {
    fetchUserStats();
  }, []);

  return (
    <FruitifyContainer data-testid={"teacher-dashboard"}>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        sx={{ mb: 3 }}
      >
        <ProfilePicture editable={false} />

        <Box>
          <Typography variant="h3">
            Welcome back, {user.info.firstName}!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {roleAssignment.school.name}
          </Typography>
        </Box>

        <Box
          display="flex"
          gap={2}
          sx={{
            ml: "auto",
            mt: { xs: 2, sm: 0 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button
            fullWidth={{ xs: true, sm: false }}
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/assignments/create")}
          >
            Create Assignment
          </Button>
          <Button
            fullWidth={{ xs: true, sm: false }}
            variant="outlined"
            startIcon={<Publish />}
            onClick={() => navigate("/assignments")}
          >
            Upload Answers
          </Button>
        </Box>
      </Box>

      {hasApprovedSubscription ? (
        <DashboardStats stats={userStats} />
      ) : (
        <UnapprovedSubscriptionChecklist status={approvalChecklist} />
      )}
      <Card variant="outlined" sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6">
            Create with Bud <BetaFeatureChip sx={{ ml: 1 }} />
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Describe what you need, then review your draft before publishing.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <CreateWithBudButton kind="assignment">
              Create an assignment with Bud
            </CreateWithBudButton>
            <CreateWithBudButton kind="quiz">
              Create a quiz with Bud
            </CreateWithBudButton>
          </Stack>
        </CardContent>
      </Card>
      {/* TODO: if no assignments do we want a quick add button? */}
      <UpcomingAssignmentsAndQuizzes
        assignments={assignments}
        quizzes={quizGroups}
      />
    </FruitifyContainer>
  );
};

export default withPageTracking(TeacherDashboard, {
  feature: "teacher-dashboard",
});
